/**
 * Multi-select mode of the map element. The element owns the set arithmetic
 * (toggle, lasso union, select all, select none) and the two non-map ways to
 * choose: the toolbar and a checkbox list that is the keyboard path. The
 * registered slot only draws the chosen set and reports clicks and lassos.
 */
import React from 'react';
import { FmButton } from '../FmButton';
import { FmCheckbox } from '../FmCheckbox';
import type { FmStoryMapMultiSelection } from './elementMedia.types';
import type { FmMapSelectionModel } from './mapSelectionModel';
import './FmMapSelection.css';

function countText(selection: FmStoryMapMultiSelection): string {
  return selection.labels.count
    .replace('{selected}', String(selection.selectedIds.length))
    .replace('{total}', String(selection.features.length));
}

/** Count, lasso, select all and select none, above the map frame. */
export const FmMapSelectionToolbar: React.FC<{
  selection: FmStoryMapMultiSelection;
  model: FmMapSelectionModel;
  testId: string;
}> = ({ selection, model, testId }) => {
  const { labels, features, selectedIds, onSelectionChange } = selection;
  return (
    <div className="fm-element-selection__toolbar" data-testid={`${testId}-selection-toolbar`}>
      <p
        className="fm-element-selection__count"
        aria-live="polite"
        data-testid={`${testId}-selection-count`}
      >
        {model.lassoActive ? labels.lassoActive : countText(selection)}
      </p>
      <div className="fm-element-selection__actions">
        <FmButton
          size="sm"
          variant={model.lassoActive ? 'primary' : 'bordered'}
          aria-pressed={model.lassoActive}
          onClick={() => model.setLassoActive(!model.lassoActive)}
          data-testid={`${testId}-selection-lasso`}
        >
          {labels.lasso}
        </FmButton>
        <FmButton
          size="sm"
          variant="bordered"
          disabled={selectedIds.length === features.length}
          onClick={() => onSelectionChange(features.map((feature) => feature.id))}
          data-testid={`${testId}-selection-all`}
        >
          {labels.selectAll}
        </FmButton>
        <FmButton
          size="sm"
          variant="bordered"
          disabled={selectedIds.length === 0}
          onClick={() => onSelectionChange([])}
          data-testid={`${testId}-selection-none`}
        >
          {labels.selectNone}
        </FmButton>
      </div>
    </div>
  );
};

/** Every choosable feature as a checkbox: the keyboard and screen-reader path. */
export const FmMapSelectionList: React.FC<{
  selection: FmStoryMapMultiSelection;
  model: FmMapSelectionModel;
  testId: string;
}> = ({ selection, model, testId }) => {
  const selected = new Set(selection.selectedIds);
  return (
    <fieldset className="fm-element-selection__list" data-testid={`${testId}-selection-list`}>
      <legend className="fm-element-selection__legend">{selection.labels.list}</legend>
      {selection.features.map((feature) => (
        <div
          key={feature.id}
          className="fm-element-selection__item"
          data-selected={selected.has(feature.id) || undefined}
        >
          <FmCheckbox
            checked={selected.has(feature.id)}
            onChange={() => model.slotProps.onFeatureSelect?.(feature.id)}
            text={feature.label}
            ariaLabel={feature.detail ? `${feature.label} · ${feature.detail}` : undefined}
            data-testid={`${testId}-selection-item-${feature.id}`}
          />
          {feature.detail ? (
            <span className="fm-element-selection__detail">{feature.detail}</span>
          ) : null}
        </div>
      ))}
    </fieldset>
  );
};
